import React, { useState, useEffect, useRef, ComponentType } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import Body from './Body/Body';
import HeaderWrapper from './Header/HeaderWrapper';
import HeaderRow from './Header/HeaderRow';
import DraggableHeaderRow from './Header/DraggableHeaderRow';
import FooterWrapper from './Footer/FooterWrapper';
import FooterRow from './Footer/FooterRow';
import SkeletonRows from './SkeletonRows/SkeletonRows';
import useColumnDragAndDrop from './hooks/useColumnDragAndDrop';
import { shouldShowLoader } from './helpers';
import { DEFAULT_EMPTY_MESSAGE } from './constants';
import { IColumn, IRowData, ISortChange } from './interfaces';
import styles from './dataTable.module.scss';

export interface IProps {
  /**
   * CSS class name for the scrolling container that wraps the table.
   * is not styleable from outside.
   */
  scrollContainerClassName?: string,
  /**
   * CSS class name for the table.
   * Provides custom styling for the table.
   */
  className?: string,
  /**
   * Inline styles for the table.
   */
  style?: React.CSSProperties,
  /**
   * Inline styles for the table header.
   */
  headerStyle?: object,
  /**
   * Array of column definitions for the table.
   */
  columns: IColumn[],
  /**
   * Array of row data to be displayed in the table.
   */
  data?: IRowData[],
  /**
   * Custom component to display while data is loading.
   */
  loadingComponent?: React.ElementType,
  /**
   * Flag indicating if data is being fetched.
   */
  isFetching?: boolean,
  /**
   * Callbacks for row events, keyed by event type.
   * Example: { onClick: (event, rowData) => void }
   */
  rowCallbacks?: {
    [key: string]: (event: Event, rowData: IRowData) => void,
  },
  /**
   * Callback to determine CSS class name for a row based on row data.
   */
  rowClassNameCallback?: (rowData: IRowData) => string,
  /**
   * Column key to sort by.
   */
  sortBy?: string,
  /**
   * Sort order, either 'asc' or 'desc'.
   */
  sortOrder?: string,
  /**
   * Callback triggered when sort order changes.
   */
  onSortChange?: ISortChange,
  /**
   * Flag to show a message when there is no data.
   */
  showEmptyStateMessage?: boolean,
  /**
   * Message or component to display when there is no data.
   */
  emptyMessage?: string | React.ReactNode,
  /**
   * CSS class name for the empty state message.
   */
  emptyMessageClassName?: string,
  /**
   * Callback triggered when row order changes.
   */
  orderCallback?: (oldIndex: number, newIndex: number) => void,
  /**
   * Flag to show or hide the table header.
   */
  isHeaderVisible?: boolean,
  /**
   * Flag to enable draggable rows.
   */
  isDraggable?: boolean,
  /**
   * Flag to use a pure component for updates.
   */
  isUsePureUpdateComponent?: boolean,
  /**
   * Flag to show the empty state message.
   */
  showEmptyStateMessageFirst?: boolean,
  /**
   * Array of selected row data.
   */
  selectedRows?: IRowData[],
  /**
   * Unique identifier for testing purposes.
   */
  dataTest: string,
  /**
   * Flag to show or hide the table footer.
   */
  isFooterVisible?: boolean,
  /**
   * Flag to enable table scrolling.
   */
  isScrollable?: boolean,
  /**
   * Height of the scrollable table.
   */
  scrollableTableHeight?: number,
  /**
   * Custom function to render the table header row.
   */
  getCustomHeaderRow?: (params) => React.ReactNode,
  /**
   * Ref object for the data table container.
   */
  dataTableRef?: React.RefObject<HTMLDivElement>,
  /**
   * Flag to enable animations.
   */
  isAnimation?: boolean,
  /**
   * Wrapper component for rows, useful for adding additional props.
   */
  rowWrapper?: <P extends {}>(WrappedComponent: ComponentType<P>) => ComponentType<P & { selectedRows: IRowData[] }>,
  /**
   * Custom content for rows.
   */
  rows?: React.ReactNode,
  /**
   * Custom function to render the table body.
   */
  bodyComponent?: (columns: IColumn[]) => React.ReactNode,
  /**
   * Custom properties for table cells.
   */
  customCellProps?: { [key: string]: any },
  /**
   * Row's unique key, could be a string or function that returns a string.
   * Default: 'key'
   */
  rowKey?: string | ((rowData: IRowData, index: number) => string),
  draggingClassName?: string,
  topNeighborClassName?: string,
  bottomNeighborClassName?: string,
  /**
   * Custom Footer component
   */
  Footer?: React.ElementType,
  /**
   * When true, applies striped background color to odd rows.
   * @default true
   */
  striped?: boolean,
  /**
   * When true, applies `white-space: nowrap` to all table body cells.
   */
  noWrapCells?: boolean,
  /**
   * Flag to enable draggable column headers for reordering.
   */
  isHeaderDraggable?: boolean,
  /**
   * Callback triggered when column order changes via drag and drop.
   */
  onColumnOrderChange?: (newOrder: string[]) => void,
  /**
   * Initial column order from external source (e.g. server).
   * Array of column names in desired order. Only draggable columns should be included.
   */
  initialColumnOrder?: string[],
  /**
   * Row's unique dataTest, could be a string or function that returns a string.
   * @default row[indexOfRow] => row[1], row[2]
   */
  dataTestRow?: string | ((rowDat: IRowData, index: number) => string),
  /**
   * Skeleton loading configuration.
   * When `skeleton.isEnabled: true` and `isFetching: true` skeleton rows are rendered instead of the table content.
   * The number of skeleton columns is derived from the `columns` prop but can be limited by `maxColumnsCount`.
   * @default { isEnabled: false, rowsCount: 6, maxColumnsCount: 6 }
   */
  skeleton?: {
    isEnabled: boolean,
    rowsCount?: number,
    maxColumnsCount?: number,
  },
}

const DataTable = ({
  className,
  headerStyle,
  style,
  columns,
  data = [],
  dataTest,
  scrollableTableHeight,
  loadingComponent: LoadingComponent,
  isFetching,
  sortBy,
  sortOrder = 'asc',
  scrollContainerClassName = styles.dataTableContainer,
  rowCallbacks = {},
  rowClassNameCallback,
  onSortChange = noop,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  emptyMessageClassName = styles.emptyMessage,
  orderCallback = noop,
  isHeaderVisible = true,
  isDraggable = false,
  isUsePureUpdateComponent = false,
  showEmptyStateMessage = true,
  showEmptyStateMessageFirst = false,
  selectedRows = [],
  isFooterVisible = false,
  isScrollable = false,
  getCustomHeaderRow,
  dataTableRef,
  isAnimation,
  rowWrapper,
  rows,
  bodyComponent,
  customCellProps,
  draggingClassName,
  topNeighborClassName,
  bottomNeighborClassName,
  rowKey = 'key',
  Footer,
  isHeaderDraggable = false,
  onColumnOrderChange,
  initialColumnOrder,
  noWrapCells = false,
  striped = false,
  dataTestRow = null,
  skeleton: {
    isEnabled: isSkeletonEnabled,
    rowsCount: skeletonRowsCount = 6,
    maxColumnsCount: skeletonMaxColumnsCount = 6,
  } = {
    isEnabled: false,
  },
}: IProps) => {
  const [showEmptyStateMessageState, setShowEmptyStateMessageState] = useState(showEmptyStateMessageFirst);
  const tableRef = useRef<HTMLTableElement>(null);

  const {
    orderedColumns,
    activeColumnName,
    sensors,
    onDragStart,
    onDragOver,
    onDragEnd,
  } = useColumnDragAndDrop({
    columns,
    isHeaderDraggable,
    initialColumnOrder,
    onColumnOrderChange,
    tableRef,
  });

  useEffect(() => () => {
    isFetching && setShowEmptyStateMessageState(true);
  }, [isFetching]);

  const HeaderRowComponent = isHeaderDraggable ? DraggableHeaderRow : HeaderRow;

  const header = (
    <HeaderWrapper style={ headerStyle }>
      { getCustomHeaderRow ? (
        getCustomHeaderRow({
          columns: orderedColumns,
          sortBy,
          sortOrder,
          onSortChange,
        })
      ) : (
        <HeaderRowComponent
          columns={ orderedColumns }
          sortBy={ sortBy }
          sortOrder={ sortOrder }
          onSortChange={ onSortChange }
        />
      ) }
    </HeaderWrapper>
  );

  const body = (
    <Body
      columns={ orderedColumns }
      data={ data }
      dataTest={ dataTest }
      rowCallbacks={ rowCallbacks }
      rowClassNameCallback={ rowClassNameCallback }
      emptyMessage={ emptyMessage }
      emptyMessageClassName={ emptyMessageClassName }
      orderCallback={ orderCallback }
      isDraggable={ isDraggable }
      draggingClassName={ draggingClassName }
      topNeighborClassName={ topNeighborClassName }
      bottomNeighborClassName={ bottomNeighborClassName }
      isUsePureUpdateComponent={ isUsePureUpdateComponent }
      showEmptyStateMessage={ showEmptyStateMessage && showEmptyStateMessageState }
      selectedRows={ selectedRows }
      isAnimation={ isAnimation }
      rowWrapper={ rowWrapper }
      rows={ rows }
      bodyComponent={ bodyComponent }
      customCellProps={ customCellProps }
      rowKey={ rowKey }
      dataTestRow={ dataTestRow }
    />
  );

  const footer = Footer ? <Footer columns={ orderedColumns } data={ data } /> : (
    <FooterWrapper>
      <FooterRow columns={ orderedColumns } />
    </FooterWrapper>
  );

  const tableContent = (
    <table
      ref={ tableRef }
      className={ cn(styles.dataTable, className, {
        [styles.isStripedOdd]: striped,
      }) }
      data-test={ `${ dataTest }${ isFetching ? 'Fetching' : '' }` }
    >
      { isHeaderDraggable && activeColumnName && (
        <colgroup>
          { orderedColumns.map((col) => (
            <col
              key={ col.name }
              className={ cn({ [styles.isDraggingColumn]: col.name === activeColumnName }) }
            />
          )) }
        </colgroup>
      ) }
      { isSkeletonEnabled && isFetching ? (
        <SkeletonRows
          columns={ columns }
          rowCount={ skeletonRowsCount }
          maxColumnsCount={ skeletonMaxColumnsCount }
        />
      ) : (
        <>
          { isHeaderVisible && header }
          { body }
          { isFooterVisible && footer }
        </>
      ) }
    </table>
  );

  return (
    <div
      className={ styles.dataTableOuter }
      data-test={ `${ dataTest }Container` }
    >
      <div
        className={ cn(scrollContainerClassName, {
          [styles.scrollableBody]: isScrollable,
          [styles.nowrapCells]: noWrapCells,
        }) }
        ref={ dataTableRef }
        style={ {
          ...style,
          height: scrollableTableHeight,
          maxHeight: scrollableTableHeight,
        } }
      >
        { isHeaderDraggable ? (
          <DndContext
            sensors={ sensors }
            collisionDetection={ closestCenter }
            modifiers={ [restrictToHorizontalAxis, restrictToParentElement] }
            onDragStart={ onDragStart }
            onDragOver={ onDragOver }
            onDragEnd={ onDragEnd }
          >
            { tableContent }
          </DndContext>
        ) : tableContent }
      </div>
      { shouldShowLoader({ isSkeletonEnabled, isFetching }) && LoadingComponent && (
        <LoadingComponent overlay />
      ) }
    </div>
  );
};

export default React.forwardRef((props: Omit<IProps, 'dataTableRef'>, ref: React.RefObject<HTMLDivElement>) => (
  <DataTable
    { ...props }
    dataTableRef={ ref }
  />
));
